import React, { useState } from 'react';
import styled from 'styled-components';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../firebase/config';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 80px 0 160px 0;
  box-sizing: border-box;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  margin: auto;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
`;

const Title = styled.h2`
  color: var(--color-text-primary);
  margin-bottom: 24px;
  font-size: 1.3rem;
  font-weight: bold;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  margin-bottom: 16px;
`;

const Label = styled.label`
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
  &::placeholder {
    color: #999;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.primary ? 'var(--color-accent-strong)' : '#bbb'};
  color: ${props => props.primary ? 'white' : 'white'};
  &:hover {
    background: ${props => props.primary ? 'var(--color-accent)' : '#999'};
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.primary ? 'rgba(var(--color-accent-rgb), 0.3)' : 'rgba(187, 187, 187, 0.6)'};
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
`;

const StatusMessage = styled.div`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
`;

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({ isOpen, onClose, userEmail }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canChange = currentPassword && newPassword && newPassword.length >= 6 && newPassword === newPasswordConfirm;

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user || !userEmail) {
        setError('로그인이 필요합니다.');
        setIsLoading(false);
        return;
      }
      // 1. 재인증
      const credential = EmailAuthProvider.credential(userEmail, currentPassword);
      await reauthenticateWithCredential(user, credential);
      // 2. 비밀번호 변경
      await updatePassword(user, newPassword);
      setSuccess('비밀번호가 성공적으로 변경되었습니다!');
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (err: any) {
      let msg = '비밀번호 변경에 실패했습니다.';
      if (err.code === 'auth/wrong-password') msg = '현재 비밀번호가 올바르지 않습니다.';
      else if (err.code === 'auth/weak-password') msg = '새 비밀번호는 6자 이상이어야 합니다.';
      else if (err.code === 'auth/too-many-requests') msg = '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay />
      <ModalWrapper>
        <ModalContent>
          <Title>비밀번호 변경</Title>
          <InputGroup>
            <Label>현재 비밀번호</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="현재 비밀번호 입력"
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>새 비밀번호</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="6자 이상 입력"
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>새 비밀번호 확인</Label>
            <Input
              type="password"
              value={newPasswordConfirm}
              onChange={e => setNewPasswordConfirm(e.target.value)}
              placeholder="새 비밀번호를 다시 입력"
              required
            />
            {newPassword && newPasswordConfirm && newPassword !== newPasswordConfirm && (
              <ErrorMessage>새 비밀번호가 일치하지 않습니다.</ErrorMessage>
            )}
          </InputGroup>
          <ButtonGroup>
            <Button primary disabled={!canChange || isLoading} onClick={handleChangePassword}>
              {isLoading ? '변경 중...' : '비밀번호 변경'}
            </Button>
            <Button onClick={handleCancel} disabled={isLoading}>취소</Button>
          </ButtonGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <StatusMessage>{success}</StatusMessage>}
        </ModalContent>
      </ModalWrapper>
    </>
  );
};

export default PasswordChangeModal; 